import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { type BillingDataSubscriptionInfo, BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';

export const formatSubscriptionSpace = (subscription: BillingDataSubscriptionInfo): string => {
    return subscription.space?.value || `${subscription.space?.space ?? ''} ${subscription.space?.units ?? ''}`.trim();
};

export const formatSubscriptionExpiresDate = (expires: number): string => {
    return format(new Date(expires), 'd MMMM yyyy', { locale: ru });
};

export const getAutoRenewalDisabledBannerTitle = (subscription: BillingDataSubscriptionInfo): string => {
    const space = formatSubscriptionSpace(subscription);
    const expiresDate = formatSubscriptionExpiresDate(subscription.expires);

    return `Без автоплатежа подписка Mail Space на ${space} отключится ${expiresDate}`;
};

export const getUnbindCardBannerTitle = (subscription: BillingDataSubscriptionInfo): string => {
    const space = formatSubscriptionSpace(subscription);
    const expiresDate = formatSubscriptionExpiresDate(subscription.expires);

    return `Без привязанной карты подписка Mail Space на ${space} отключится ${expiresDate}`;
};

export const getActiveWebSubscription = (subscriptions: BillingDataSubscriptionInfo[]): BillingDataSubscriptionInfo | undefined => {
    return subscriptions
        .filter(
            (sub) =>
                sub.type === BillingDataPlatform.web &&
                (sub.status === BillingDataSubscriptionInfoStatus.ACTIVE || sub.status === BillingDataSubscriptionInfoStatus.SUSPENDED) &&
                !sub.isFreemium
        )
        .sort((a, b) => a.expires - b.expires)[0];
};

export const getSubscriptionWithDisabledAutoRenewal = (
    subscriptions: BillingDataSubscriptionInfo[]
): BillingDataSubscriptionInfo | undefined => {
    return subscriptions
        .filter(
            (sub) =>
                sub.type === BillingDataPlatform.web &&
                (sub.status === BillingDataSubscriptionInfoStatus.ACTIVE || sub.status === BillingDataSubscriptionInfoStatus.SUSPENDED) &&
                !sub.isFreemium &&
                sub.autorenewal === false
        )
        .sort((a, b) => a.expires - b.expires)[0];
};

const getCardTail = (cardNumber?: string): string => {
    if (!cardNumber) {
        return '';
    }

    const digits = cardNumber.replace(/\D/g, '');
    return digits.slice(-4);
};

export const getCardAddedSnackbarText = (cardNumber: string | undefined, expires: number, withNextPayment = true): string => {
    const cardTail = getCardTail(cardNumber);
    const cardText = cardTail ? `Карта •••• ${cardTail} добавлена` : 'Карта добавлена';

    if (!withNextPayment) {
        return cardText;
    }

    const expiresDate = formatSubscriptionExpiresDate(expires);

    return `${cardText}.\nСледующее списание ${expiresDate}`;
};

export const getAutoRenewalEnabledSnackbarText = (expires: number): string => {
    const expiresDate = formatSubscriptionExpiresDate(expires);

    return `Автоплатёж включён.\nСледующее списание ${expiresDate}`;
};

export const getCardAddedAndAutoRenewalEnabledSnackbarText = (cardNumber: string | undefined, expires: number): string => {
    const cardTail = getCardTail(cardNumber);
    const cardText = cardTail ? `Карта •••• ${cardTail}` : 'Карта';
    const nextPaymentDate = formatSubscriptionNextPaymentDate(expires);

    return `${cardText} добавлена, автоплатёж включён. Следующее списание ${nextPaymentDate}`;
};

export const formatSubscriptionNextPaymentDate = (expires: number): string => {
    return format(new Date(expires), 'd MMMM yyyy', { locale: ru });
};

export const getAutoRenewalEnabledFromHomeBannerSnackbarText = (expires: number): string => {
    const nextPaymentDate = formatSubscriptionNextPaymentDate(expires);

    return `Автоплатёж включён.\nСледующее списание ${nextPaymentDate}`;
};
